import { useState, useEffect, useMemo } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { PortfolioItem } from '@/types/models'
import type { VectorSearchResult } from '@/lib/vectorSearchApi'

interface UseSearchTattooFeedProps {
  searchResults: VectorSearchResult[]
  isSearching: boolean
  searchError: string | null
  collections: ('flashes' | 'portfolioItems' | 'healedPortfolioItems')[]
}

export function useSearchTattooFeed({ searchResults, isSearching, searchError, collections }: UseSearchTattooFeedProps) {
  const [items, setItems] = useState<PortfolioItem[]>([])
  const [searchScores, setSearchScores] = useState<Map<string, number>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  
  // Track the current request ID to ignore stale results
  const currentRequestIdRef = useState(() => ({ current: 0 }))[0]

  // Create a stable reference for searchResults to prevent infinite loops
  // Only recreate when the actual content changes, not when the array reference changes
  const stableSearchResults = useMemo(() => {
    // Create a stable string representation of the search results for comparison
    const resultsKey = searchResults.map(result => `${result.id}:${result.score}`).join('|')
    return { results: searchResults, key: resultsKey }
  }, [searchResults.length, searchResults.map(r => `${r.id}:${r.score}`).join('|')])

  useEffect(() => {
    // Increment request ID for this new search
    currentRequestIdRef.current += 1
    const thisRequestId = currentRequestIdRef.current
    
    // Don't run if not in search mode (empty search results and not searching)
    if (searchResults.length === 0 && !isSearching && !searchError) {
      setItems([])
      setLoading(false)
      setError(null)
      return
    }

    if (isSearching) {
      setLoading(true)
      setError(null)
      setItems([]) // Clear items while searching
      setSearchScores(new Map())
      return
    }

    // If there's a search error, don't show any results
    if (searchError) {
      setItems([])
      setLoading(false)
      setError(searchError)
      return
    }

    if (searchResults.length === 0) {
      setItems([])
      setLoading(false)
      return
    }

    // Extract portfolio item IDs and their types from search results
    const itemsWithTypes = searchResults.map(result => {
      // Split by underscore and take the last part as the portfolio item ID
      const parts = result.id.split('_')
      const itemId = parts.length > 1 ? parts[parts.length - 1] : result.id
      // Map types to collection names: 'flash' -> 'flashes', 'healedPortfolioItem' -> 'healedPortfolioItems', otherwise -> 'portfolioItems'
      const collectionName = result.type === 'flash' ? 'flashes' : result.type === 'healedPortfolioItem' ? 'healedPortfolioItems' : 'portfolioItems'
      return { itemId, collectionName, score: result.score }
    })

    setLoading(true)
    setError(null)
    
    console.log('[useSearchTattooFeed] Starting fetch with requestId:', thisRequestId)

    // Fetch items by IDs from their specific collections (determined by type field)
    const fetchItems = async (itemsWithTypes: Array<{ itemId: string; collectionName: string; score: number }>): Promise<PortfolioItem[]> => {
      if (itemsWithTypes.length === 0) return []

      console.log('[useSearchTattooFeed] Fetching items by IDs:', { 
        count: itemsWithTypes.length,
        breakdown: {
          flashes: itemsWithTypes.filter(i => i.collectionName === 'flashes').length,
          portfolioItems: itemsWithTypes.filter(i => i.collectionName === 'portfolioItems').length,
          healedPortfolioItems: itemsWithTypes.filter(i => i.collectionName === 'healedPortfolioItems').length,
        }
      })

      try {
        // Fetch each item by ID from its specific collection
        const itemPromises = itemsWithTypes.map(async ({ itemId, collectionName }) => {
          const docRef = doc(db, collectionName, itemId)
          const docSnap = await getDoc(docRef)
          
          if (docSnap.exists()) {
            const data = docSnap.data()
            const portfolioItem: PortfolioItem = {
              id: docSnap.id,
              artistId: data.artistId as string,
              type: (data.type as PortfolioItem["type"]) ?? "image",
              storagePath: (data.storagePath as string) ?? "",
              imageUrl: data.imageUrl as string,
              width: (data.width as number) ?? 0,
              height: (data.height as number) ?? 0,
              createdAt: data.createdAt?.toDate?.() ?? new Date(),
            }
            
            // Add optional properties only if they exist
            if (data.compressedImageUrl) portfolioItem.compressedImageUrl = data.compressedImageUrl
            if (data.styles) portfolioItem.styleTags = data.styles as string[]
            if (data.caption) portfolioItem.caption = data.caption as string
            if (data.timedSaves) portfolioItem.timedSaves = data.timedSaves as Array<{
              itemId: string
              userId: string
              savedAt: Date
            }>
            
            // Flash-specific properties
            if (collectionName === 'flashes') {
              portfolioItem.isFlash = true
              if (data.minPrice) portfolioItem.minPrice = data.minPrice
              if (data.minSize) portfolioItem.minSize = data.minSize
              if (data.available !== undefined) portfolioItem.available = data.available
              if (data.booked !== undefined) portfolioItem.booked = data.booked
              if (data.accessCode) portfolioItem.accessCode = data.accessCode
              if (data.eventId) portfolioItem.eventId = data.eventId
              if (data.eventExclusive !== undefined) portfolioItem.eventExclusive = data.eventExclusive
            }
            
            // Healed portfolio-specific properties
            if (collectionName === 'healedPortfolioItems') {
              portfolioItem.isHealed = true
            }
            
            return portfolioItem
          }
          
          // Not found
          console.warn(`[useSearchTattooFeed] Item not found: ${itemId} in ${collectionName}`)
          return null
        })

        const results = await Promise.all(itemPromises)
        const validItems = results.filter((item): item is PortfolioItem => item !== null)
        
        console.log('[useSearchTattooFeed] Fetched items:', {
          requested: itemsWithTypes.length,
          found: validItems.length,
          missing: itemsWithTypes.length - validItems.length
        })

        return validItems
      } catch (error) {
        console.error('[useSearchTattooFeed] Error fetching items:', error)
        throw error
      }
    }

    fetchItems(itemsWithTypes)
      .then(portfolioItems => {
        // Check if this request is still current
        if (thisRequestId !== currentRequestIdRef.current) {
          console.log('[useSearchTattooFeed] Ignoring stale results for requestId:', thisRequestId, 'current:', currentRequestIdRef.current)
          return
        }
        
        // Maintain the order from search results, filtering out any missing items
        const orderedItems = itemsWithTypes
          .map(({ itemId }) => portfolioItems.find(item => item.id === itemId))
          .filter((item): item is PortfolioItem => !!item)

        // Build a map of item IDs to their search scores
        const scoresMap = new Map<string, number>()
        itemsWithTypes.forEach(({ itemId, score }) => {
          scoresMap.set(itemId, score)
        })

        console.log('[useSearchTattooFeed] Setting ordered items:', {
          searchResultsCount: searchResults.length,
          portfolioItemsCount: portfolioItems.length,
          orderedItemsCount: orderedItems.length,
          itemIdsOrder: itemsWithTypes.slice(0, 5).map(i => i.itemId), // First 5 IDs
          orderedItemsIds: orderedItems.slice(0, 5).map(item => item.id), // First 5 ordered IDs
          searchResultIds: searchResults.slice(0, 5).map(r => r.id), // First 5 search result IDs
          requestId: thisRequestId
        })

        setItems(orderedItems)
        setSearchScores(scoresMap)
      })
      .catch(err => {
        // Only set error if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          console.error('[useSearchTattooFeed] Error loading search results:', err)
          setError(err)
          setItems([])
        }
      })
      .finally(() => {
        // Only clear loading if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setLoading(false)
        }
      })

  }, [stableSearchResults.key, isSearching, searchError, collections])

  return {
    items,
    loading,
    error,
    hasMore: false, // Search results don't support pagination
    loadingMore: false,
    loadMoreItems: () => Promise.resolve(), // No-op for search results
    initialLoadComplete: !loading && !isSearching,
    searchScores
  }
}
